import { z } from 'zod';
import { contentIntelligenceTicketQualityAssessedAssessmentSchema } from './content-intelligence-ticket-quality-dto';

export const DEFAULT_CONTENT_INTELLIGENCE_TICKET_QUALITY_HISTORY_LIMIT = 100;
export const MAX_CONTENT_INTELLIGENCE_TICKET_QUALITY_HISTORY_LIMIT = 200;

export const contentIntelligenceTicketQualityHistoryQuerySchema = z.object({
  itemId: z.coerce.number().int().positive().optional(),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_CONTENT_INTELLIGENCE_TICKET_QUALITY_HISTORY_LIMIT)
    .default(DEFAULT_CONTENT_INTELLIGENCE_TICKET_QUALITY_HISTORY_LIMIT),
});

export const contentIntelligenceTicketQualitySnapshotSchema =
  contentIntelligenceTicketQualityAssessedAssessmentSchema.omit({ status: true }).extend({
    historyId: z.string().min(1),
    evaluationRunId: z.string().min(1).nullable(),
  });

export const contentIntelligenceTicketQualityHistoryResponseSchema = z
  .object({
    loopId: z.number().int().positive(),
    itemId: z.number().int().positive().nullable(),
    total: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    truncated: z.boolean(),
    items: z
      .array(contentIntelligenceTicketQualitySnapshotSchema)
      .max(MAX_CONTENT_INTELLIGENCE_TICKET_QUALITY_HISTORY_LIMIT),
    dataSource: z.literal('sir-ticket-quality-history'),
  })
  .superRefine((response, context) => {
    if (response.returned !== response.items.length) {
      context.addIssue({
        code: 'custom',
        message: 'Returned count must equal the number of history items',
        path: ['returned'],
      });
    }
    if (response.total < response.returned) {
      context.addIssue({
        code: 'custom',
        message: 'Total count cannot be lower than the returned count',
        path: ['total'],
      });
    }

    const shouldBeTruncated = response.total > response.returned;
    if (response.truncated !== shouldBeTruncated) {
      context.addIssue({
        code: 'custom',
        message: 'Truncated must report whether more history is available',
        path: ['truncated'],
      });
    }
    for (const [index, snapshot] of response.items.entries()) {
      if (snapshot.loopId !== response.loopId) {
        context.addIssue({
          code: 'custom',
          message: 'Snapshot loop must match the requested loop',
          path: ['items', index, 'loopId'],
        });
      }
      if (response.itemId !== null && snapshot.itemId !== response.itemId) {
        context.addIssue({
          code: 'custom',
          message: 'Snapshot item must match the requested item',
          path: ['items', index, 'itemId'],
        });
      }
    }
  });

export type ContentIntelligenceTicketQualitySnapshot = z.infer<
  typeof contentIntelligenceTicketQualitySnapshotSchema
>;
export type ContentIntelligenceTicketQualityHistoryResponse = z.infer<
  typeof contentIntelligenceTicketQualityHistoryResponseSchema
>;
